import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import ComexHistoryChart from "../components/ComexHistoryChart";
import BrazilMapChart from "../components/BrazilMapChart";
import KPICard from "../components/ui/KPICard";
import SEO from "../components/SEO";
import Card from "../components/ui/Card";
import { OilMarketSection, OilMacroSection } from "../components/OilSection";
import OilStructuralBreaks from "../components/OilStructuralBreaks";
import { TrendingUp, TrendingDown, RefreshCw, DollarSign, Users, Building2, MapPin, ChevronDown, ChevronUp, BarChart3, Vote, UserCheck, Stethoscope, ClipboardList } from "lucide-react";
import { Link } from "react-router-dom";

// Hook para detectar tamanho da tela
const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia(query).matches;
    }
    return false;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const media = window.matchMedia(query);
    setMatches(media.matches);

    const listener = (event) => setMatches(event.matches);

    if (media.addListener) {
      media.addListener(listener);
      return () => media.removeListener(listener);
    } else {
      media.addEventListener('change', listener);
      return () => media.removeEventListener('change', listener);
    }
  }, [query]);

  return matches;
};

export default function DadosPage() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const isMobile = useMediaQuery('(max-width: 768px)');

  const [selectedYear, setSelectedYear] = useState(2025);

  // População
  const [popBrasil, setPopBrasil] = useState(null);
  const [popEstados, setPopEstados] = useState([]);
  const [popTopCidades, setPopTopCidades] = useState([]);
  const [showAllEstados, setShowAllEstados] = useState(false);
  const [showAllCidades, setShowAllCidades] = useState(false);

  // PIB
  const [pibData, setPibData] = useState([]);
  const [pibResumo, setPibResumo] = useState(null);
  const [pibTopCidades, setPibTopCidades] = useState([]);
  const [showAllPibEstados, setShowAllPibEstados] = useState(false);
  const [showAllPibCidades, setShowAllPibCidades] = useState(false);

  // Políticos
  const [polData, setPolData] = useState(null);
  const [showAllPartidos, setShowAllPartidos] = useState(false);

  // Saúde (Med)
  const [medSummary, setMedSummary] = useState(null);
  const [showAllMedUf, setShowAllMedUf] = useState(false);
  const [showAllMedServicos, setShowAllMedServicos] = useState(false);

  // Nav section tracking
  const [activeSection, setActiveSection] = useState("sec-comex");

  const fetchSummary = useCallback(async (showFullLoading = true) => {
    try {
      if (showFullLoading) setLoading(true);
      else setIsRefreshing(true);

      const res = await axios.get(`/comex/summary/${selectedYear}`);
      if (res.data.success) {
        setSummary(res.data.data);
      }
    } catch (err) {
      console.error("Erro ao carregar dados do Comex", err);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [selectedYear]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  // Busca dados de população e PIB uma vez
  useEffect(() => {
    (async () => {
      try {
        const [estRes, rankRes, pibRes, pibResumoRes, pibRankRes, polRes, medRes] = await Promise.all([
          axios.get("/populacao/estados"),
          axios.get("/populacao/ranking", { params: { limit: 50 } }),
          axios.get("/pib/estados", { params: { ano: 2021 } }).catch(() => null),
          axios.get("/pib/resumo", { params: { ano: 2021 } }).catch(() => null),
          axios.get("/pib/ranking", { params: { limit: 20, ano: 2021 } }).catch(() => null),
          axios.get("/politicos/resumo").catch(() => null),
          axios.get("/med/summary").catch(() => null),
        ]);
        if (estRes.data?.success) {
          const all = estRes.data.data || [];
          const brasil = all.find((e) => e.tipo === "pais");
          if (brasil) setPopBrasil(brasil);
          setPopEstados(all.filter((e) => e.tipo === "estado").sort((a, b) => b.populacao - a.populacao));
        }
        if (rankRes.data?.success) {
          setPopTopCidades(rankRes.data.data || []);
        }
        if (pibRes?.data?.success) {
          setPibData(pibRes.data.data || []);
        }
        if (pibResumoRes?.data?.success) {
          setPibResumo(pibResumoRes.data.data || null);
        }
        if (pibRankRes?.data?.success) {
          setPibTopCidades(pibRankRes.data.data || []);
        }
        if (polRes?.data?.success) {
          setPolData(polRes.data.data || null);
        }
        if (medRes?.data?.success) {
          setMedSummary(medRes.data.data || null);
        }
      } catch (err) {
        console.error("Erro ao carregar dados de população/PIB:", err);
      }
    })();
  }, []);

  const formatUSD = (val) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      notation: "compact",
    }).format(val);

  const formatPop = (val) =>
    new Intl.NumberFormat("pt-BR").format(val);

  const formatBRL = (val) => {
    if (!val || val === 0) return "R$ 0";
    if (Math.abs(val) >= 1e9) return `R$ ${(val / 1e9).toFixed(1)} tri`;
    if (Math.abs(val) >= 1e6) return `R$ ${(val / 1e6).toFixed(1)} bi`;
    if (Math.abs(val) >= 1e3) return `R$ ${(val / 1e3).toFixed(1)} mi`;
    return `R$ ${val.toLocaleString("pt-BR", { maximumFractionDigits: 0 })} mil`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--accent)] mx-auto mb-4"></div>
          <p className="text-[var(--muted)]">Consultando banco de dados...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <SEO
        title="Dados Econômicos e Sociais do Brasil"
        description="Comércio exterior, população, PIB municipal e outros indicadores públicos do Brasil, organizados por estado e município com fontes oficiais (Comex Stat, IBGE)."
        path="/dados"
      />
      {/* Section Nav */}
      <nav className="sticky top-0 z-10 bg-[var(--bg)]/90 backdrop-blur-sm border-b border-[var(--border)] mb-8 -mx-4 px-4 sm:-mx-6 sm:px-6">
        <div className="flex gap-1 overflow-x-auto py-2" style={{ scrollbarWidth: 'none' }}>
          {[
            { id: "sec-comex", label: "Comércio Exterior" },
            { id: "sec-populacao", label: "População" },
            { id: "sec-pib", label: "PIB" },
            { id: "sec-politicos", label: "Políticos" },
            { id: "sec-saude", label: "Saúde" },
            { id: "sec-petroleo", label: "Petróleo" },
            { id: "sec-macroeconomia", label: "Macroeconomia" },
          ].map(({ id, label }) => (
            <button
              key={id}
              onClick={() => { setActiveSection(id); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              className={`whitespace-nowrap px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                activeSection === id
                  ? "bg-[var(--accent)] text-white"
                  : "text-[var(--muted)] hover:text-[var(--text)] hover:bg-[var(--border)]"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </nav>

      {/* ===================== Paine: Comércio Exterior ===================== */}
      {activeSection === "sec-comex" && (
        <div>
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-[var(--text)] mb-2">
              Balança Comercial Brasileira
            </h1>
            <p className="text-[var(--muted)]">
              Dados de comércio exterior por estado
            </p>
          </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KPICard
          label="Exportado"
          value={formatUSD(summary?.export)}
          icon={<TrendingUp size={20} />}
          className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0"
        />
        <KPICard
          label="Importado"
          value={formatUSD(summary?.import)}
          icon={<TrendingDown size={20} />}
          className="bg-gradient-to-br from-orange-500 to-orange-600 text-white border-0"
        />
        <KPICard
          label="Saldo Comercial"
          value={formatUSD(summary?.balance)}
          icon={<DollarSign size={20} />}
          className={`${summary?.balance >= 0
            ? 'bg-gradient-to-br from-blue-500 to-blue-600'
            : 'bg-gradient-to-br from-red-500 to-red-600'
            } text-white border-0`}
        />

        {/* Seletor de Ano */}
        <Card className="bg-gradient-to-br from-slate-600 to-slate-700 text-white border-0">
          <div>
            <h3 className="text-xs uppercase tracking-wide opacity-80 mb-3">Ano</h3>
            <div className="flex gap-2 items-center">
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="flex-1 px-3 py-2 rounded-lg border border-white/30 bg-white/20 text-white text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-white/50 cursor-pointer"
              >
                {Array.from({ length: 27 }, (_, i) => 2000 + i).reverse().map((y) => (
                  <option key={y} value={y} className="bg-slate-700">{y}</option>
                ))}
              </select>
              <button
                onClick={() => fetchSummary(false)}
                disabled={isRefreshing}
                className={`p-2 rounded-lg border border-white/30 ${isRefreshing
                  ? 'bg-white/20 opacity-60 cursor-not-allowed'
                  : 'bg-white/20 hover:bg-white/30'
                  } text-white transition-colors`}
              >
                <RefreshCw size={18} className={isRefreshing ? 'animate-spin' : ''} />
              </button>
            </div>
          </div>
        </Card>
      </div>

      {/* Gráfico de Histórico */}
      <div className="mb-6">
        <ComexHistoryChart isMobile={isMobile} />
      </div>

      {/* Mapa do Brasil */}
      {summary?.states && (
        <div className="mb-8">
          <BrazilMapChart statesData={summary.states} year={selectedYear} isMobile={isMobile} popData={popEstados} pibData={pibData} />
        </div>
      )}
        </div>
      )}

      {/* ===================== Paine: População ===================== */}
      {activeSection === "sec-populacao" && (
        <div>
          {popBrasil && (
            <>
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-[var(--text)] mb-2">
                  População Brasileira
                </h2>
                <p className="text-[var(--muted)]">
                  Estimativas populacionais — IBGE {popBrasil.anoReferencia}
                </p>
              </div>

          {/* KPIs de População */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <KPICard
              label="População Total"
              value={formatPop(popBrasil.populacao)}
              icon={<Users size={20} />}
              className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0"
            />
            <KPICard
              label="Estados"
              value={popEstados.length}
              icon={<MapPin size={20} />}
              className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white border-0"
            />
            <KPICard
              label="Municípios"
              value={popTopCidades.length >= 50 ? "5.571+" : formatPop(popTopCidades.length)}
              icon={<Building2 size={20} />}
              className="bg-gradient-to-br from-cyan-500 to-cyan-600 text-white border-0"
            />
            <KPICard
              label="Maior Cidade"
              value={popTopCidades[0]?.nome || "—"}
              change={popTopCidades[0] ? `${formatPop(popTopCidades[0].populacao)} hab.` : ""}
              changeType="neutral"
              icon={<TrendingUp size={20} />}
              className="bg-gradient-to-br from-rose-500 to-rose-600 text-white border-0"
            />
          </div>

          {/* Rankings lado a lado */}
          <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-2'} gap-6 mb-6`}>
            {/* Ranking de Estados */}
            <Card title="População por Estado" subtitle={`${popEstados.length} unidades federativas`}>
              <div className="space-y-1">
                {(showAllEstados ? popEstados : popEstados.slice(0, 10)).map((est, i) => {
                  const pct = popBrasil.populacao > 0 ? (est.populacao / popBrasil.populacao) * 100 : 0;
                  return (
                    <div key={est.sigla || i} className="flex items-center gap-3 py-1.5 group">
                      <span className="text-xs font-mono text-[var(--muted)] w-6 text-right">{i + 1}.</span>
                      <span className="text-xs font-semibold text-[var(--accent)] w-7">{est.sigla}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-sm text-[var(--text)] truncate">{est.nome}</span>
                          <span className="text-xs text-[var(--muted)] ml-2 whitespace-nowrap">{formatPop(est.populacao)}</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-[var(--border)] overflow-hidden">
                          <div
                            className="h-full rounded-full bg-gradient-to-r from-purple-500 to-indigo-500 transition-all duration-500"
                            style={{ width: `${Math.max(pct, 0.5)}%` }}
                          />
                        </div>
                      </div>
                      <span className="text-xs text-[var(--muted)] w-12 text-right">{pct.toFixed(1)}%</span>
                    </div>
                  );
                })}
              </div>
              {popEstados.length > 10 && (
                <button
                  onClick={() => setShowAllEstados((v) => !v)}
                  className="mt-3 flex items-center gap-1 text-sm text-[var(--accent)] hover:underline mx-auto"
                >
                  {showAllEstados ? <><ChevronUp size={14} /> Mostrar menos</> : <><ChevronDown size={14} /> Ver todos os {popEstados.length} estados</>}
                </button>
              )}
            </Card>

            {/* Ranking de Cidades */}
            <Card title="Maiores Cidades" subtitle="Top municípios por população">
              <div className="space-y-1">
                {(showAllCidades ? popTopCidades : popTopCidades.slice(0, 10)).map((cid, i) => {
                  const maxPop = popTopCidades[0]?.populacao || 1;
                  const pct = (cid.populacao / maxPop) * 100;
                  return (
                    <Link key={cid.codigoIbge || i} to={`/municipio/${cid.codigoIbge}`} className="flex items-center gap-3 py-1.5 group hover:bg-[var(--border)]/30 rounded-lg px-1 transition-colors">
                      <span className="text-xs font-mono text-[var(--muted)] w-6 text-right">{cid.posicao || i + 1}.</span>
                      <span className="text-xs font-semibold text-[var(--accent)] w-7">{cid.uf}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-sm text-[var(--text)] truncate">{cid.nome}</span>
                          <span className="text-xs text-[var(--muted)] ml-2 whitespace-nowrap">{formatPop(cid.populacao)}</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-[var(--border)] overflow-hidden">
                          <div
                            className="h-full rounded-full bg-gradient-to-r from-rose-500 to-orange-400 transition-all duration-500"
                            style={{ width: `${Math.max(pct, 0.5)}%` }}
                          />
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
              {popTopCidades.length > 10 && (
                <button
                  onClick={() => setShowAllCidades((v) => !v)}
                  className="mt-3 flex items-center gap-1 text-sm text-[var(--accent)] hover:underline mx-auto"
                >
                  {showAllCidades ? <><ChevronUp size={14} /> Mostrar menos</> : <><ChevronDown size={14} /> Ver top {popTopCidades.length} cidades</>}
                </button>
              )}
            </Card>
          </div>
            </>
          )}
        </div>
      )}

      {/* ===================== Paine: PIB ===================== */}
      {activeSection === "sec-pib" && (
        <div>
          {pibResumo && (
            <>
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-[var(--text)] mb-2">
                  PIB Municipal
                </h2>
                <p className="text-[var(--muted)]">
                  Produto Interno Bruto — IBGE/SIDRA 2021
                </p>
              </div>

          {/* KPIs de PIB */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <KPICard
              label="PIB Brasil"
              value={formatBRL(pibResumo.pibTotal)}
              icon={<BarChart3 size={20} />}
              className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white border-0"
            />
            <KPICard
              label="PIB per capita médio"
              value={`R$ ${((pibResumo.pibPerCapitaMedio || 0) * 1000).toLocaleString("pt-BR", { maximumFractionDigits: 0 })}`}
              icon={<DollarSign size={20} />}
              className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0"
            />
            <KPICard
              label="Municípios"
              value={formatPop(pibResumo.totalMunicipios)}
              icon={<Building2 size={20} />}
              className="bg-gradient-to-br from-teal-500 to-teal-600 text-white border-0"
            />
            <KPICard
              label="Maior PIB"
              value={pibTopCidades[0]?.municipio || "—"}
              change={pibTopCidades[0] ? formatBRL(pibTopCidades[0].pibTotal) : ""}
              changeType="neutral"
              icon={<TrendingUp size={20} />}
              className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0"
            />
          </div>

          {/* Rankings PIB lado a lado */}
          <div className={`grid ${isMobile ? "grid-cols-1" : "grid-cols-2"} gap-6 mb-6`}>
            {/* Ranking por Estado */}
            <Card title="PIB por Estado" subtitle={`${pibData.length} unidades federativas`}>
              <div className="space-y-1">
                {(showAllPibEstados ? pibData : pibData.slice(0, 10)).map((est, i) => {
                  const pct =
                    pibResumo.pibTotal > 0 ? (est.pibTotal / pibResumo.pibTotal) * 100 : 0;
                  return (
                    <Link
                      key={est.uf || i}
                      to={`/estado/${est.uf}/2025`}
                      className="flex items-center gap-3 py-1.5 group hover:bg-[var(--border)]/30 rounded-lg px-1 transition-colors"
                    >
                      <span className="text-xs font-mono text-[var(--muted)] w-6 text-right">
                        {i + 1}.
                      </span>
                      <span className="text-xs font-semibold text-[var(--accent)] w-7">
                        {est.uf}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-sm text-[var(--text)] truncate">
                            {formatBRL(est.pibTotal)}
                          </span>
                          <span className="text-xs text-[var(--muted)] ml-2 whitespace-nowrap">
                            {pct.toFixed(1)}%
                          </span>
                        </div>
                        <div className="h-1.5 rounded-full bg-[var(--border)] overflow-hidden">
                          <div
                            className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all duration-500"
                            style={{ width: `${Math.max(pct, 0.5)}%` }}
                          />
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
              {pibData.length > 10 && (
                <button
                  onClick={() => setShowAllPibEstados((v) => !v)}
                  className="mt-3 flex items-center gap-1 text-sm text-[var(--accent)] hover:underline mx-auto"
                >
                  {showAllPibEstados ? (
                    <>
                      <ChevronUp size={14} /> Mostrar menos
                    </>
                  ) : (
                    <>
                      <ChevronDown size={14} /> Ver todos os {pibData.length} estados
                    </>
                  )}
                </button>
              )}
            </Card>

            {/* Top Cidades por PIB */}
            <Card title="Maiores PIBs Municipais" subtitle="Top municípios por PIB total">
              <div className="space-y-1">
                {(showAllPibCidades ? pibTopCidades : pibTopCidades.slice(0, 10)).map((cid, i) => {
                  const maxPib = pibTopCidades[0]?.pibTotal || 1;
                  const pct = (cid.pibTotal / maxPib) * 100;
                  return (
                    <Link
                      key={cid.codigoIbge || i}
                      to={`/municipio/${cid.codigoIbge}`}
                      className="flex items-center gap-3 py-1.5 group hover:bg-[var(--border)]/30 rounded-lg px-1 transition-colors"
                    >
                      <span className="text-xs font-mono text-[var(--muted)] w-6 text-right">
                        {cid.posicao || i + 1}.
                      </span>
                      <span className="text-xs font-semibold text-[var(--accent)] w-7">
                        {cid.uf}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-sm text-[var(--text)] truncate">
                            {cid.municipio}
                          </span>
                          <span className="text-xs text-[var(--muted)] ml-2 whitespace-nowrap">
                            {formatBRL(cid.pibTotal)}
                          </span>
                        </div>
                        <div className="h-1.5 rounded-full bg-[var(--border)] overflow-hidden">
                          <div
                            className="h-full rounded-full bg-gradient-to-r from-green-500 to-emerald-400 transition-all duration-500"
                            style={{ width: `${Math.max(pct, 0.5)}%` }}
                          />
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
              {pibTopCidades.length > 10 && (
                <button
                  onClick={() => setShowAllPibCidades((v) => !v)}
                  className="mt-3 flex items-center gap-1 text-sm text-[var(--accent)] hover:underline mx-auto"
                >
                  {showAllPibCidades ? (
                    <>
                      <ChevronUp size={14} /> Mostrar menos
                    </>
                  ) : (
                    <>
                      <ChevronDown size={14} /> Ver top {pibTopCidades.length} cidades
                    </>
                  )}
                </button>
              )}
            </Card>
          </div>
        </>
      )}
        </div>
      )}

      {/* ===================== Paine: Políticos ===================== */}
      {activeSection === "sec-politicos" && (
        <div>
          {polData && (
            <>
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-[var(--text)] mb-2">
                  Candidatos — TSE
                </h2>
                <p className="text-[var(--muted)]">
                  Dados do Tribunal Superior Eleitoral
                </p>
              </div>

          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <KPICard
              label="Total de Candidatos"
              value={formatPop(polData.total)}
              icon={<Vote size={20} />}
              className="bg-gradient-to-br from-violet-500 to-violet-600 text-white border-0"
            />
            <KPICard
              label="Partidos"
              value={polData.totalPartidos}
              icon={<Building2 size={20} />}
              className="bg-gradient-to-br from-fuchsia-500 to-fuchsia-600 text-white border-0"
            />
            {polData.porGenero && (() => {
              const fem = polData.porGenero.find(g => g.genero === "FEMININO");
              const total2024 = polData.porAno?.find(a => a.ano === 2024)?.total || polData.total;
              const pctFem = fem ? ((fem.total / total2024) * 100).toFixed(1) : "0";
              return (
                <KPICard
                  label="Candidatas Mulheres"
                  value={`${pctFem}%`}
                  change={fem ? formatPop(fem.total) : "0"}
                  changeType="neutral"
                  icon={<UserCheck size={20} />}
                  className="bg-gradient-to-br from-pink-500 to-pink-600 text-white border-0"
                />
              );
            })()}
            {polData.porAno?.length > 0 && (
              <KPICard
                label={`Eleições ${polData.porAno[polData.porAno.length - 1].ano}`}
                value={formatPop(polData.porAno[polData.porAno.length - 1].total)}
                icon={<Vote size={20} />}
                className="bg-gradient-to-br from-amber-500 to-amber-600 text-white border-0"
              />
            )}
          </div>

          {/* Rankings */}
          <div className={`grid ${isMobile ? "grid-cols-1" : "grid-cols-2"} gap-6 mb-6`}>
            {/* Top Partidos */}
            <Card title="Maiores Partidos" subtitle="Por número de candidatos (última eleição)">
              <div className="space-y-1">
                {(showAllPartidos ? polData.porPartido : polData.porPartido?.slice(0, 10))?.map((p, i) => {
                  const maxTotal = polData.porPartido[0]?.total || 1;
                  const pct = (p.total / maxTotal) * 100;
                  return (
                    <div key={p.partido || i} className="flex items-center gap-3 py-1.5">
                      <span className="text-xs font-mono text-[var(--muted)] w-6 text-right">{i + 1}.</span>
                      <span className="text-xs font-semibold text-[var(--accent)] w-12">{p.partido}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-sm text-[var(--text)]">{formatPop(p.total)} candidatos</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-[var(--border)] overflow-hidden">
                          <div
                            className="h-full rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 transition-all duration-500"
                            style={{ width: `${Math.max(pct, 0.5)}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {polData.porPartido?.length > 10 && (
                <button
                  onClick={() => setShowAllPartidos((v) => !v)}
                  className="mt-3 flex items-center gap-1 text-sm text-[var(--accent)] hover:underline mx-auto"
                >
                  {showAllPartidos ? <><ChevronUp size={14} /> Mostrar menos</> : <><ChevronDown size={14} /> Ver todos</>}
                </button>
              )}
            </Card>

            {/* Diversidade */}
            <Card title="Perfil dos Candidatos" subtitle="Gênero e Cor/Raça (última eleição)">
              <div className="space-y-4">
                {/* Gênero */}
                <div>
                  <h4 className="text-sm font-semibold text-[var(--text)] mb-2">Gênero</h4>
                  <div className="space-y-1.5">
                    {polData.porGenero?.filter(g => g.genero && g.genero !== "NÃO DIVULGÁVEL").map((g, i) => {
                      const total2024 = polData.porAno?.find(a => a.ano === 2024)?.total || polData.total;
                      const pct = total2024 > 0 ? (g.total / total2024) * 100 : 0;
                      const colors = { MASCULINO: "from-blue-500 to-blue-400", FEMININO: "from-pink-500 to-pink-400" };
                      return (
                        <div key={g.genero || i} className="flex items-center gap-3">
                          <span className="text-sm text-[var(--text)] w-20">{g.genero === "MASCULINO" ? "Masculino" : "Feminino"}</span>
                          <div className="flex-1">
                            <div className="h-2 rounded-full bg-[var(--border)] overflow-hidden">
                              <div
                                className={`h-full rounded-full bg-gradient-to-r ${colors[g.genero] || "from-gray-400 to-gray-300"} transition-all duration-500`}
                                style={{ width: `${Math.max(pct, 0.5)}%` }}
                              />
                            </div>
                          </div>
                          <span className="text-xs text-[var(--muted)] w-16 text-right">{pct.toFixed(1)}%</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Cor/Raça */}
                <div>
                  <h4 className="text-sm font-semibold text-[var(--text)] mb-2">Cor / Raça</h4>
                  <div className="space-y-1.5">
                    {polData.porRaca?.filter(r => r.raca && r.raca !== "NÃO DIVULGÁVEL" && r.raca !== "SEM INFORMAÇÃO").map((r, i) => {
                      const total2024 = polData.porAno?.find(a => a.ano === 2024)?.total || polData.total;
                      const pct = total2024 > 0 ? (r.total / total2024) * 100 : 0;
                      const racaColors = {
                        BRANCA: "from-slate-400 to-slate-300", PRETA: "from-gray-700 to-gray-600",
                        PARDA: "from-amber-500 to-amber-400", AMARELA: "from-yellow-400 to-yellow-300",
                        INDÍGENA: "from-red-500 to-red-400",
                      };
                      return (
                        <div key={r.raca || i} className="flex items-center gap-3">
                          <span className="text-sm text-[var(--text)] w-20 capitalize">{(r.raca || "").toLowerCase()}</span>
                          <div className="flex-1">
                            <div className="h-2 rounded-full bg-[var(--border)] overflow-hidden">
                              <div
                                className={`h-full rounded-full bg-gradient-to-r ${racaColors[r.raca] || "from-gray-400 to-gray-300"} transition-all duration-500`}
                                style={{ width: `${Math.max(pct, 0.5)}%` }}
                              />
                            </div>
                          </div>
                          <span className="text-xs text-[var(--muted)] w-16 text-right">{pct.toFixed(1)}%</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </>
      )}
        </div>
      )}

      {/* ===================== Paine: Saúde ===================== */}
      {activeSection === "sec-saude" && (
        <div>
          {medSummary && (
            <>
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-[var(--text)] mb-2">
                  Estabelecimentos de Saúde
                </h2>
                <p className="text-[var(--muted)]">
                  Hospitais, clínicas e postos de saúde — CNES/DATASUS
                </p>
              </div>

              {/* KPIs de Saúde */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <KPICard
                  label="Estabelecimentos"
                  value={formatPop(medSummary.total)}
                  icon={<Stethoscope size={20} />}
                  className="bg-gradient-to-br from-sky-500 to-sky-600 text-white border-0"
                />
                <KPICard
                  label="Estados"
                  value={medSummary.totalUf}
                  icon={<MapPin size={20} />}
                  className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white border-0"
                />
                <KPICard
                  label="Municípios"
                  value={formatPop(medSummary.totalMunicipios)}
                  icon={<Building2 size={20} />}
                  className="bg-gradient-to-br from-cyan-500 to-cyan-600 text-white border-0"
                />
                <KPICard
                  label="Serviços Especializados"
                  value={medSummary.totalServicos}
                  icon={<ClipboardList size={20} />}
                  className="bg-gradient-to-br from-teal-500 to-teal-600 text-white border-0"
                />
              </div>

              {/* Rankings lado a lado */}
              <div className={`grid ${isMobile ? "grid-cols-1" : "grid-cols-2"} gap-6 mb-6`}>
                {/* Estabelecimentos por Estado */}
                <Card title="Estabelecimentos por Estado" subtitle={`${medSummary.porUf?.length || 0} unidades federativas`}>
                  <div className="space-y-1">
                    {(showAllMedUf ? medSummary.porUf : medSummary.porUf?.slice(0, 10))?.map((est, i) => {
                      const maxTotal = medSummary.porUf[0]?.total || 1;
                      const pct = (est.total / maxTotal) * 100;
                      return (
                        <div key={est.uf || i} className="flex items-center gap-3 py-1.5">
                          <span className="text-xs font-mono text-[var(--muted)] w-6 text-right">{i + 1}.</span>
                          <span className="text-xs font-semibold text-[var(--accent)] w-7">{est.uf}</span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-0.5">
                              <span className="text-sm text-[var(--text)]">{formatPop(est.total)} estabelecimentos</span>
                            </div>
                            <div className="h-1.5 rounded-full bg-[var(--border)] overflow-hidden">
                              <div
                                className="h-full rounded-full bg-gradient-to-r from-sky-500 to-cyan-500 transition-all duration-500"
                                style={{ width: `${Math.max(pct, 0.5)}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {medSummary.porUf?.length > 10 && (
                    <button
                      onClick={() => setShowAllMedUf((v) => !v)}
                      className="mt-3 flex items-center gap-1 text-sm text-[var(--accent)] hover:underline mx-auto"
                    >
                      {showAllMedUf ? <><ChevronUp size={14} /> Mostrar menos</> : <><ChevronDown size={14} /> Ver todos os {medSummary.porUf.length} estados</>}
                    </button>
                  )}
                </Card>

                {/* Principais Serviços */}
                <Card title="Principais Serviços" subtitle="Por número de estabelecimentos">
                  <div className="space-y-1">
                    {(showAllMedServicos ? medSummary.porServico : medSummary.porServico?.slice(0, 10))?.map((s, i) => {
                      const maxTotal = medSummary.porServico[0]?.total || 1;
                      const pct = (s.total / maxTotal) * 100;
                      const nomeCurto = (s.servicoNome || "").toLowerCase().replace(/(^|\s)\S/g, (l) => l.toUpperCase());
                      return (
                        <div key={s.servicoCodigo || i} className="flex items-center gap-3 py-1.5">
                          <span className="text-xs font-mono text-[var(--muted)] w-6 text-right">{i + 1}.</span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-0.5">
                              <span className="text-sm text-[var(--text)] truncate" title={nomeCurto}>{nomeCurto}</span>
                              <span className="text-xs text-[var(--muted)] ml-2 whitespace-nowrap">{formatPop(s.total)}</span>
                            </div>
                            <div className="h-1.5 rounded-full bg-[var(--border)] overflow-hidden">
                              <div
                                className="h-full rounded-full bg-gradient-to-r from-teal-500 to-emerald-400 transition-all duration-500"
                                style={{ width: `${Math.max(pct, 0.5)}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {medSummary.porServico?.length > 10 && (
                    <button
                      onClick={() => setShowAllMedServicos((v) => !v)}
                      className="mt-3 flex items-center gap-1 text-sm text-[var(--accent)] hover:underline mx-auto"
                    >
                      {showAllMedServicos ? <><ChevronUp size={14} /> Mostrar menos</> : <><ChevronDown size={14} /> Ver todos os {medSummary.porServico.length} serviços</>}
                    </button>
                  )}
                </Card>
              </div>

              <Link
                to="/med"
                className="inline-flex items-center gap-2 text-sm text-[var(--accent)] hover:underline"
              >
                Ver busca completa de estabelecimentos →
              </Link>
            </>
          )}
        </div>
      )}

      {/* ===================== Paine: Petróleo ===================== */}
      {activeSection === "sec-petroleo" && (
        <div className="flex flex-col gap-8">
          <OilMarketSection />
          <OilStructuralBreaks />
        </div>
      )}

      {/* ===================== Paine: Macroeconomia ===================== */}
      {activeSection === "sec-macroeconomia" && (
        <div>
          <OilMacroSection />
        </div>
      )}
    </div>
  );
}
